import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { ChatMessage, CitationTarget, CodexNotification, FileRecord, ThreadRecord, DocumentVisualizationSource, ReaderVisualizationState } from '@reados/contracts';
import { prepareTranscriptFrame, type FrameAssets } from './frame';
import { projectTranscript, type PendingTurn } from './projection';
import { parseFrameMessage } from './protocol';
import { resolveReference, targetFromPayload } from './references';
import { MediaTicketCache } from './media';
import { inspectionResources } from './inspection';
import { VideoFrameCache } from './video-frames';
import { createSelectionAnchor, resolveSelectionAction, type TranscriptSelectionAction } from './selection';
import { ImageLightbox, type LightboxImage } from './ImageLightbox';
import { ImageSourceResolver } from './image-sources';
import { pdfExtractNavigationRequest, pdfExtractNavigationTarget } from './pdf-extract-preview';
import { generatedImageReference } from './image-generation-preview';
import { imageCommandPreviews } from './image-command-preview.ts';
import { acceptedImageReference } from './conversation-input';
import { BilibiliMetadataCache } from './video-metadata';
import { resolveTranscriptFork, type TranscriptForkAction } from './assistant-footer';
import type { RendererTheme } from './presentation';
import {authorizeVisualization,loadVisualizationAssets,normalizeVisualizationState,readVisualizationArtifact,readDocumentVisualization,visualizationReaderStates,dispatchDocumentFollowUp,dispatchVisualizationFollowUp,visualizationDocument,type DocumentFollowUpSource,type VisualizationAnchor,type VisualizationAssets} from './visualizations';
import { resolveTranscriptAsyncQuestionAnswer, type TranscriptAsyncQuestionAnswer } from './async-questions';

interface ImageSession { images: LightboxImage[]; index: number; requestId: string; scope: string; sequence: number }
export interface TranscriptScrollState { canScrollToBottom: boolean }
const EMPTY_EVENTS: CodexNotification[] = [];
const EMPTY_FILES: FileRecord[] = [];

export interface ReadOSTranscriptProps {
  messages: ChatMessage[]; events?: CodexNotification[]; files?: FileRecord[];
  /** Administrator-owned read scope; never a target-user login credential. */
  inspectionId?: string;
  /** Disables mutation callbacks. Without an inspection scope, user resources fail closed. */
  readOnly?: boolean;
  /** Ends an inspection when its server scope is revoked or expired. */
  onAccessDenied?: (message:string) => void;
  onNavigate?: (target: CitationTarget) => void;
  onFollowUp?: (text: string, source?: DocumentFollowUpSource) => void;
  onOpenAgent?: (threadId: string) => void;
  onSelectionAction?: (selection: TranscriptSelectionAction) => void;
  onFork?: (action: TranscriptForkAction) => void | Promise<void>;
  onAsyncQuestionAnswer?: (action: TranscriptAsyncQuestionAnswer) => Promise<void>;
  asyncQuestionDisabled?: boolean;
  forkDisabled?: boolean;
  forkNotices?: ThreadRecord['forkNotices'];
  states?: Record<string, unknown>;
  onStateChange?: (states: Record<string, unknown>) => void;
  visualizationSource?: DocumentVisualizationSource;
  onVisualizationsChange?: (items: ReaderVisualizationState[]) => void;
  assetBaseUrl?: string;
  pendingTurn?: PendingTurn;
  /** Empty conversation UI replaces preparation text while the iframe warms up. Errors retain priority. */
  emptyState?: React.ReactNode;
  /** Stable conversation identity, including empty conversations. */
  transcriptScope?: string;
  onScrollStateChange?: (state: TranscriptScrollState) => void;
  /** Increment to request the original ReadOS scroll-to-bottom command. */
  scrollToBottomRequest?: number;
  /** Document preview shares the renderer while using top-first reading layout. */
  presentation?: 'conversation' | 'document';
  referenceBasePath?: string;
  title?: string;
  visible?: boolean;
  /** Changes appearance in place, preserving the mounted transcript and DIL state. */
  theme?: RendererTheme;
}

export function ReadOSTranscript({ messages, events = EMPTY_EVENTS, files = EMPTY_FILES, onNavigate, onFollowUp, onOpenAgent, onSelectionAction, onFork, onAsyncQuestionAnswer, asyncQuestionDisabled = false, forkDisabled = false, forkNotices, states: savedStates, onStateChange, visualizationSource, onVisualizationsChange, assetBaseUrl = '/vendor/reados/', pendingTurn, emptyState, transcriptScope, onScrollStateChange, scrollToBottomRequest, presentation = 'conversation', referenceBasePath, title, visible = true, theme = 'light', inspectionId, readOnly = false, onAccessDenied }: ReadOSTranscriptProps) {
  const inspectionMode = readOnly || inspectionId !== undefined;
  const iframe = useRef<HTMLIFrameElement>(null);
  const currentTheme = useRef(theme); currentTheme.current = theme;
  const token = useRef(crypto.randomUUID());
  const [assets, setAssets] = useState<FrameAssets | null>(null);
  const [frameHTML, setFrameHTML] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const [forking, setForking] = useState<{ scope: string; messageID: string } | null>(null);
  const forkRequest = useRef<{ scope: string; messageID: string } | null>(null);
  const asyncQuestionRequests = useRef(new Map<string, 'pending' | 'accepted'>());
  const [imageSession, setImageSession] = useState<ImageSession | null>(null);
  const imageSequence = useRef(0);
  const refreshImage = useRef<(image: LightboxImage) => Promise<string | null>>(async () => null);
  const sourceScope = transcriptScope || messages[0]?.id || 'empty';
  const scope = inspectionMode ? `${inspectionId ?? 'readonly'}:${sourceScope}` : sourceScope;
  const lastScrollState = useRef<boolean | null>(null);
  const lastScrollRequest = useRef({ scope, value: scrollToBottomRequest });
  const selectionAnchor = useRef<{ id: string; update: ReturnType<typeof createSelectionAnchor>['update'] } | null>(null);
  const states = useRef(new Map<string, Record<string, unknown>>());
  const payload = useMemo(() => projectTranscript(messages, events, pendingTurn, forkNotices), [messages, events, pendingTurn, forkNotices]);
  const visualizationRevision=useMemo(()=>JSON.stringify(messages.filter(message=>message.role==='assistant'&&message.status!=='streaming').map(message=>[message.id,message.text])),[messages]);
  const forkEnabled = !inspectionMode && presentation === 'conversation' && !!onFork;
  const asyncQuestionEnabled = !inspectionMode && presentation === 'conversation' && !!onAsyncQuestionAnswer;
  const latest = useRef({ files, messages, events, payload, onNavigate, onFollowUp, onOpenAgent, onSelectionAction, onFork, forkDisabled, onAsyncQuestionAnswer, asyncQuestionDisabled, presentation, savedStates, onStateChange, visualizationSource, onVisualizationsChange, onScrollStateChange, referenceBasePath, visible, assets,theme,visualizationRevision,inspectionMode,inspectionId,onAccessDenied });
  latest.current = { files, messages, events, payload, onNavigate, onFollowUp, onOpenAgent, onSelectionAction, onFork, forkDisabled, onAsyncQuestionAnswer, asyncQuestionDisabled, presentation, savedStates, onStateChange, visualizationSource, onVisualizationsChange, onScrollStateChange, referenceBasePath, visible, assets,theme,visualizationRevision,inspectionMode,inspectionId,onAccessDenied };
  const currentScope = useRef(scope); currentScope.current = scope;
  const refreshVisualizationContext=useRef<()=>void>(()=>{});

  useEffect(() => { imageSequence.current++; setImageSession(null); }, [scope, assetBaseUrl]);

  useEffect(() => {
    setReady(false);
    selectionAnchor.current?.update(null); selectionAnchor.current = null;
    lastScrollState.current = false;
    lastScrollRequest.current = { scope, value: scrollToBottomRequest };
    latest.current.onScrollStateChange?.({ canScrollToBottom: false });
    // A different conversation gets a fresh iframe/coordinator. A detached
    // history position or in-flight animation must never own the next chat.
  }, [scope, assetBaseUrl, inspectionId, inspectionMode]);

  useEffect(() => {
    let cancelled = false; setReady(false); setError(''); setAssets(null); setFrameHTML(null);
    void prepareTranscriptFrame(assetBaseUrl, token.current, location.origin, presentation, () => currentTheme.current, inspectionMode).then(async value => {
      if (cancelled) return;
      setFrameHTML(value.html);
      const dil = await value.dil;
      if (!cancelled) setAssets({ html: value.html, assetBaseUrl:value.assetBaseUrl, dil });
    })
      .catch(reason => { if (!cancelled) setError(reason instanceof Error ? reason.message : '聊天渲染器加载失败'); });
    return () => { cancelled = true; };
  }, [assetBaseUrl, presentation, inspectionMode]);

  useEffect(() => {
    const resources = inspectionMode ? inspectionResources(inspectionId, location.origin, fetch.bind(globalThis), message=>{
      if(!disposed&&latest.current.inspectionMode===inspectionMode&&latest.current.inspectionId===inspectionId)latest.current.onAccessDenied?.(message);
    }) : undefined;
    const media = new MediaTicketCache(location.origin, fetch.bind(globalThis), resources);
    const videoMetadata = new BilibiliMetadataCache(location.origin);
    const videoFrames = new VideoFrameCache(media);
    const frameRequests = new Map<string, AbortController>();
    const visualizationArtifacts = new Map<string, {fragment:string;hash:string}>();
    const visualizationFailures=new Map<string,string>();
    const visualizationAnchors = new Map<string,VisualizationAnchor>();
    const visualizationStateKeys=new Map<string,string>();
    const visualizationControllers=new Set<AbortController>();
    let visualizationAssets:Promise<VisualizationAssets>|undefined,visualizationSequence=0,visualizationRevision='';
    const assetController=new AbortController();
    const imageSources = new ImageSourceResolver(media, location.origin, (reference, source) => generatedImageReference(
      reference ?? acceptedImageReference(source, latest.current.payload.messages, latest.current.files, location.origin), latest.current.events));
    let disposed = false,contextSignature='';
    const readerVisualizationContext=()=>{
      const context=latest.current;
      if(disposed||!context.visualizationSource||!context.visible||context.inspectionMode)return [];
      const stored={...context.savedStates,...states.current.get(currentScope.current)};
      return visualizationReaderStates(context.visualizationSource,[...visualizationAnchors.values()]
        .filter(item=>item.visible&&!visualizationFailures.has(item.path)&&authorizeVisualization(item,context.messages,context.payload,context.visualizationSource))
        .flatMap(item=>{const artifact=visualizationArtifacts.get(item.path),key=visualizationStateKeys.get(item.id);
          return artifact&&key?[{reference:item.path,ordinal:item.ordinal,title:item.title,contentRevision:artifact.hash,state:stored[key]}]:[];}));
    };
    const publishVisualizationContext=()=>{
      const context=readerVisualizationContext(),serialized=JSON.stringify(context);
      if(serialized!==contextSignature){contextSignature=serialized;latest.current.onVisualizationsChange?.(context);}
    };
    refreshVisualizationContext.current=publishVisualizationContext;
    publishVisualizationContext();
    refreshImage.current = async image => {
      const sequence = imageSequence.current;
      const resolved = await imageSources.resolve({ source: image.id, title: image.title }, latest.current.files, true, latest.current.referenceBasePath);
      return !disposed && sequence === imageSequence.current ? resolved.source || null : null;
    };
    const listener = async (event: MessageEvent) => {
      // Opaque sandbox origins serialize as "null". Window identity + random
      // capability + schema are all required; nested DIL frames fail identity.
      if (event.source !== iframe.current?.contentWindow || event.origin !== 'null') return;
      const data = parseFrameMessage(event.data);
      if (!data || data.token !== token.current || latest.current.inspectionMode !== inspectionMode || latest.current.inspectionId !== inspectionId) return;
      // Enforcement is independent of UI controls and supplied callbacks.
      if (inspectionMode && ['followUp','selectionAction','openAgent','fork','asyncQuestionAnswer','state'].includes(data.type)) return;
      const context = latest.current;
      const send = (value: object) => iframe.current?.contentWindow?.postMessage({ channel: 'reados-renderer-v1', token: token.current, ...value }, '*');
      if (data.type === 'ready') { setReady(true); return; }
      if (data.type === 'error') { setError(data.message); return; }
      if (data.type === 'rendered') { setError(''); return; }
      if (data.type === 'visualizationSync' || data.type === 'visualizationRetry') {
        if(data.scope!==currentScope.current||!context.assets?.assetBaseUrl)return;
        for(const controller of visualizationControllers)controller.abort();
        const requestedScope=currentScope.current,requestedWindow=event.source,sequence=++visualizationSequence;
        const current=()=>!disposed&&sequence===visualizationSequence&&currentScope.current===requestedScope&&iframe.current?.contentWindow===requestedWindow;
        if(context.visualizationRevision!==visualizationRevision){visualizationRevision=context.visualizationRevision;visualizationArtifacts.clear();visualizationFailures.clear();visualizationStateKeys.clear();}
        if(data.type==='visualizationSync'){
          visualizationAnchors.clear();for(const item of data.items)if(authorizeVisualization(item,context.messages,context.payload,context.visualizationSource))visualizationAnchors.set(item.id,item);
        }else{const item=visualizationAnchors.get(data.id);if(!item)return;visualizationArtifacts.delete(item.path);visualizationFailures.delete(item.path);visualizationStateKeys.delete(item.id);}
        publishVisualizationContext();
        const candidates=[...visualizationAnchors.values()].filter(item=>item.near&&authorizeVisualization(item,context.messages,context.payload,context.visualizationSource)).slice(0,6);
        const controllers:AbortController[]=[];
        const hydrated=await Promise.all(candidates.map(async item=>{
          try{
            if(visualizationFailures.has(item.path))return {id:item.id,error:visualizationFailures.get(item.path)};
            let artifact=visualizationArtifacts.get(item.path);
            if(!artifact){const controller=new AbortController();controllers.push(controller);visualizationControllers.add(controller);
              try{const fragment=context.visualizationSource
                  ?await readDocumentVisualization(context.visualizationSource,item.path,controller.signal,resources?.request??fetch,inspectionMode?inspectionId:undefined)
                  :await readVisualizationArtifact(item.path,controller.signal,resources?.request??fetch,resources?.artifactUrl);const hash=[...new Uint8Array(await crypto.subtle.digest('SHA-256',new TextEncoder().encode(fragment)))].map(v=>v.toString(16).padStart(2,'0')).join('');artifact={fragment,hash};
                if(!current())return null;visualizationArtifacts.set(item.path,artifact);while(visualizationArtifacts.size>12)visualizationArtifacts.delete(visualizationArtifacts.keys().next().value!);
              }finally{visualizationControllers.delete(controller);}}
            if(!current()||!authorizeVisualization(item,latest.current.messages,latest.current.payload,latest.current.visualizationSource))return null;
            visualizationAssets??=loadVisualizationAssets(context.assets!.assetBaseUrl!,assetController.signal).catch(error=>{visualizationAssets=undefined;throw error;});
            const kit=await visualizationAssets;
            if(!current()||!authorizeVisualization(item,latest.current.messages,latest.current.payload,latest.current.visualizationSource))return null;
            const key='__visualization:'+await crypto.subtle.digest('SHA-256',new TextEncoder().encode(item.id+'|'+artifact.hash)).then(bytes=>[...new Uint8Array(bytes)].map(v=>v.toString(16).padStart(2,'0')).join(''));
            visualizationStateKeys.set(item.id,key);const stored={...latest.current.savedStates,...states.current.get(requestedScope)};
            return {id:item.id,title:item.title,html:visualizationDocument(artifact.fragment,kit,stored[key],latest.current.theme,false,!!latest.current.visualizationSource&&!!latest.current.onVisualizationsChange&&!inspectionMode),hash:artifact.hash,visible:item.visible,near:item.near};
          }catch(reason){const error=reason instanceof Error?reason.message:'可视化加载失败';if(current()){visualizationFailures.set(item.path,error);while(visualizationFailures.size>12)visualizationFailures.delete(visualizationFailures.keys().next().value!);}return {id:item.id,error};}
        }));
        if(current()){send({type:'visualization',scope:requestedScope,items:hydrated.filter(Boolean)});publishVisualizationContext();}
        return;
      }
      if(data.type==='visualizationEvent'){
        const item=visualizationAnchors.get(data.id),requestedScope=currentScope.current;
        const reply=(ok:boolean,error?:string)=>send({type:'visualizationResult',scope:requestedScope,id:data.id,requestId:data.requestId,ok,error});
        if(data.scope!==requestedScope||!item||!authorizeVisualization(item,context.messages,context.payload,context.visualizationSource)){reply(false,'可视化来源已改变');return;}
        if(inspectionMode && (data.event==='state'||data.event==='followUp')){reply(false,'当前为只读查看');return;}
        if(data.event==='state'){
          const value=normalizeVisualizationState(data.value),key=visualizationStateKeys.get(data.id);
          if(!value||!key||!context.onStateChange){reply(false,'无法保存可视化状态');return;}
          const next={...context.savedStates,...states.current.get(requestedScope),[key]:value};
          if(Object.keys(next).length>256||new TextEncoder().encode(JSON.stringify(next)).length>256000){reply(false,'可视化状态空间已满');return;}
          try{context.onStateChange(next);states.current.set(requestedScope,next);publishVisualizationContext();reply(true);}catch{reply(false,'无法保存可视化状态');}
        }else if(data.event==='export'){
          if(!context.visible||!context.assets?.assetBaseUrl){reply(false,'当前无法导出');return;}
          const artifact=visualizationArtifacts.get(item.path),requestedFrame=event.source;
          if(!artifact){reply(false,'请重新加载可视化后导出');return;}
          try{
            visualizationAssets??=loadVisualizationAssets(context.assets.assetBaseUrl,assetController.signal).catch(error=>{visualizationAssets=undefined;throw error;});
            const kit=await visualizationAssets;
            if(disposed||currentScope.current!==requestedScope||iframe.current?.contentWindow!==requestedFrame||!authorizeVisualization(item,latest.current.messages,latest.current.payload,latest.current.visualizationSource))return;
            const document=visualizationDocument(artifact.fragment,kit,null,latest.current.theme,true);
            const url=URL.createObjectURL(new Blob([document],{type:'text/html;charset=utf-8'})),link=window.document.createElement('a');
            link.href=url;link.download=item.path.split('/').at(-1)||'visualization.html';window.document.body.append(link);link.click();link.remove();setTimeout(()=>URL.revokeObjectURL(url),1000);reply(true);
          }catch{reply(false,'导出失败，请重试。');}
        }else if(data.event==='followUp'){
          const value=data.value as {prompt?:unknown;title?:unknown}|null;
          if(!context.visible||!context.onFollowUp||typeof value?.prompt!=='string'||!value.prompt.trim()||value.prompt.length>32000){reply(false,'无法发送后续问题');return;}
          const selected=readerVisualizationContext().find(state=>state.reference===item.path&&state.ordinal===item.ordinal);
          const result=dispatchVisualizationFollowUp({prompt:value.prompt,title:typeof value.title==='string'?value.title:undefined,document:context.visualizationSource,item:selected,confirm:message=>window.confirm(message),onFollowUp:context.onFollowUp});
          reply(result.ok,result.error);
        }else if(data.event==='openExternal'){
          const value=data.value as {href?:unknown}|null;
          try{const url=new URL(String(value?.href));if(!context.visible||!['https:','http:'].includes(url.protocol)||url.username||url.password||url.origin===location.origin)throw new Error();
            if(window.confirm(`打开外部链接？\n${url.href}`)){openExternal(url.href);reply(true);}else reply(false,'已取消');
          }catch{reply(false,'外部链接无效');}
        }
        return;
      }
      if (data.type === 'scrollState') {
        if (data.scope !== currentScope.current || data.canScrollToBottom === lastScrollState.current) return;
        lastScrollState.current = data.canScrollToBottom;
        context.onScrollStateChange?.({ canScrollToBottom: data.canScrollToBottom });
        return;
      }
      if (data.type === 'request') {
        if(data.kind==='plotFigure'){
          const {receiptId,outputId}=data.value;
          const image=[...imageCommandPreviews(context.events).values()].flat().flatMap(command=>command.images)
            .find(image=>image.receiptId===receiptId&&image.outputId===outputId&&image.figureSha256);
          if(!image){send({type:'response',id:data.id,value:null});return;}
          try{
            const url=resources ? await resources.figureUrl?.(image.receiptId,image.outputId) : `/api/image-commands/figures/${encodeURIComponent(image.receiptId)}/${encodeURIComponent(image.outputId)}`;
            if(!url)throw new Error('只读图表不可用');
            const response=await (resources?.request??fetch)(url,{method:'GET',credentials:'same-origin',cache:'no-store'});
            if(!response.ok)throw new Error('图表不可用');
            const bytes=await response.arrayBuffer();if(bytes.byteLength>8*1024*1024)throw new Error('图表过大');
            const fingerprint=[...new Uint8Array(await crypto.subtle.digest('SHA-256',bytes))].map(value=>value.toString(16).padStart(2,'0')).join('');
            if(fingerprint!==image.figureSha256)throw new Error('图表已改变');
            const figure=JSON.parse(new TextDecoder().decode(bytes));
            if(!Array.isArray(figure.data)||figure.data.length>100)throw new Error('图表数据无效');
            if(!disposed)send({type:'response',id:data.id,value:figure});
          }catch{if(!disposed)send({type:'response',id:data.id,value:null});}
          return;
        }
        if (data.kind === 'videoMetadata') {
          if(inspectionMode){send({type:'response',id:data.id,value:null});return;}
          const requestedScope = currentScope.current, requestedFrame = event.source;
          const metadata = await videoMetadata.get(data.value);
          if (!disposed && requestedScope === currentScope.current && iframe.current?.contentWindow === requestedFrame) send({ type: 'response', id: data.id, value: metadata });
          return;
        }
        if (data.kind === 'videoFrame') {
          const requestedScope = currentScope.current;
          const reference = typeof data.value.href === 'string' ? resolveReference(data.value.href, context.files, location.origin, context.referenceBasePath) : null;
          const time = data.value.time;
          if (!reference || reference.kind !== 'video' || typeof time !== 'number'
            || !reference.target.timeLocations?.some(point => point.start === time && (point.end === undefined || point.end === point.start))
            || frameRequests.size >= 64 || frameRequests.has(data.id)) { send({ type: 'response', id: data.id, value: null }); return; }
          const controller = new AbortController(); frameRequests.set(data.id, controller);
          try {
            const value = await videoFrames.resolve(reference, time, controller.signal);
            const current = resolveReference(data.value.href as string, latest.current.files, location.origin, latest.current.referenceBasePath);
            if (!disposed && !controller.signal.aborted && requestedScope === currentScope.current
              && current?.contentRevision === reference.contentRevision) send({ type: 'response', id: data.id, value });
          } catch { if (!disposed && !controller.signal.aborted) send({ type: 'response', id: data.id, value: null }); }
          finally { frameRequests.delete(data.id); }
          return;
        }
        const candidate = data.kind === 'resolve' && typeof data.value.href === 'string'
          ? resolveReference(data.value.href, context.files, location.origin, context.referenceBasePath)
            ?? acceptedImageReference(data.value.href, context.payload.messages, context.files, location.origin)
          : data.kind === 'resolvePayload' ? targetFromPayload(data.value.payload, context.files, location.origin, context.referenceBasePath) : null;
        const result = generatedImageReference(candidate, context.events);
        try {
          const value = result ? await media.resolve(result, data.value.refresh === true) : null;
          if (result && value) imageSources.remember(result, value);
          if (!disposed) send({ type: 'response', id: data.id, value });
        }
        catch (reason) { send({type:'response',id:data.id,value:null});setError(reason instanceof Error ? reason.message : '媒体加载失败'); }
        return;
      }
      if (data.type === 'cancelRequest') { frameRequests.get(data.id)?.abort(); frameRequests.delete(data.id); return; }
      if (data.type === 'openImages') {
        const sequence = ++imageSequence.current, requestedScope = currentScope.current;
        const images = await Promise.all(data.items.map(item => imageSources.resolve(item, context.files, false, context.referenceBasePath)));
        if (!disposed && sequence === imageSequence.current && requestedScope === currentScope.current)
          setImageSession({ images, index: data.index, requestId: data.requestId, scope: requestedScope, sequence });
        return;
      }
      if (data.type === 'navigate') {
        if(data.payload && Object.hasOwn(data.payload, 'readosPdfExtractKey')) {
          const request = pdfExtractNavigationRequest(data.payload.readosPdfExtractKey, data.payload.readosPdfExtractKind, context.messages, context.events, inspectionMode ? inspectionId ?? '' : undefined);
          if(!request) { setError('无法确认这次 PDF 阅读的来源，无法跳转。'); return; }
          const requestedScope = currentScope.current;
          try {
            const response = await (resources?.request??fetch)(request.url, request.method === 'GET' ? { method: 'GET', credentials: 'same-origin', cache: 'no-store' } : { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(request.body) });
            const result = await response.json(), target = response.ok && pdfExtractNavigationTarget(result.target);
            if(disposed || currentScope.current !== requestedScope) return;
            if(target) { setError(''); latest.current.onNavigate?.(target); }
            else setError(request.body.kind === 'extracted' ? '这次抽取的 PDF 已不可用，无法预览。' : '这次阅读的 PDF 原文版本已不可用，无法跳转。');
          } catch { if(!disposed && currentScope.current === requestedScope) setError('无法检查 PDF 阅读来源，请稍后重试。'); }
          return;
        }
        if(data.payload?.action==='openReadosLibraryAttachment'){
          const presented=context.payload.messages.find(message=>message.id===data.payload!.messageId&&message.role==='user');
          const candidates=Array.isArray(presented?.attachments)?presented.attachments:[];
          const attachment=candidates.find(value=>value&&typeof value==='object'&&(value as Record<string,unknown>).id===data.payload!.attachmentId) as Record<string,unknown>|undefined;
          if(!attachment||typeof attachment.resourceId!=='string')return;
          if(attachment.resourceKind==='folder')context.onNavigate?.({folderId:attachment.resourceId});
          else {
            const file=context.files.find(file=>file.id===attachment.resourceId);
            if(file)context.onNavigate?.({fileId:file.id,path:file.sandboxPath});
            else if(typeof attachment.virtualPath==='string'&&attachment.virtualPath.startsWith(`/workspace/.reados/chat-attachments/${attachment.resourceId}/`))context.onNavigate?.({path:attachment.virtualPath});
            else setError('文件不可用或已删除');
          }
          return;
        }
        const result = data.href ? resolveReference(data.href, context.files, location.origin, context.referenceBasePath) : targetFromPayload(data.payload, context.files, location.origin, context.referenceBasePath);
        if (result) context.onNavigate?.(result.target);
        else if (data.href) openExternal(data.href);
        return;
      }
      if (data.type === 'openUrl') {
        const result = resolveReference(data.url, context.files, location.origin, context.referenceBasePath);
        if (result) context.onNavigate?.(result.target);
        else openExternal(data.url);
        return;
      }
      if (data.type === 'followUp') {
        if(context.visualizationSource)dispatchDocumentFollowUp(data.text,{documentFileId:context.visualizationSource.fileId},context.onFollowUp);
        else context.onFollowUp?.(data.text,undefined);
        return;
      }
      if (data.type === 'openAgent') { context.onOpenAgent?.(data.threadId); return; }
      if (data.type === 'asyncQuestionAnswer') {
        const requestedScope = data.scope, requestedFrame = event.source;
        const reply = (accepted: boolean, error?: string) => {
          if (!disposed && currentScope.current === requestedScope && iframe.current?.contentWindow === requestedFrame)
            send({ type: 'asyncQuestionResult', scope: requestedScope, requestId: data.requestId, messageID: data.messageID, accepted, ...(error ? { error } : {}) });
        };
        if (requestedScope !== currentScope.current) return;
        if (context.presentation !== 'conversation' || !context.visible || !context.onAsyncQuestionAnswer || context.asyncQuestionDisabled) {
          reply(false, '当前无法发送回答，请稍后重试。'); return;
        }
        const action = resolveTranscriptAsyncQuestionAnswer(data.messageID, data.answers, context.messages, context.payload);
        if (!action) { reply(false, '这条问题已回答或来源已改变，请刷新后重试。'); return; }
        const key = JSON.stringify([requestedScope, action.threadId, action.turnId, action.messageID]);
        const previous = asyncQuestionRequests.current.get(key);
        if (previous) { reply(previous === 'accepted', previous === 'pending' ? '回答正在发送，请稍候。' : undefined); return; }
        asyncQuestionRequests.current.set(key, 'pending');
        try {
          await context.onAsyncQuestionAnswer(action);
          asyncQuestionRequests.current.set(key, 'accepted'); reply(true);
        } catch (reason) {
          asyncQuestionRequests.current.delete(key);
          reply(false, reason instanceof Error ? reason.message : '回答未发送，请重试。');
        }
        return;
      }
      if (data.type === 'fork') {
        if (data.scope !== currentScope.current || context.presentation !== 'conversation' || !context.visible
          || !context.onFork || context.forkDisabled || forkRequest.current?.scope === data.scope) return;
        const action = resolveTranscriptFork(data.messageID, context.messages, context.payload);
        if (!action) { setError('无法确认这条回复的分叉位置，请刷新后重试。'); return; }
        const request = { scope: data.scope, messageID: data.messageID };
        forkRequest.current = request; setForking(request);
        try { await context.onFork(action); }
        catch (reason) { if (currentScope.current === request.scope) setError(reason instanceof Error ? reason.message : '分叉失败，请重试。'); }
        finally {
          if (forkRequest.current === request) forkRequest.current = null;
          setForking(current => current === request ? null : current);
        }
        return;
      }
      if (data.type === 'selectionAction') {
        const { channel: _channel, token: _token, type: _type, ...input } = data;
        const selection = resolveSelectionAction(input, context.messages, context.payload);
        if (selection) {
          selectionAnchor.current?.update(null);
          selectionAnchor.current = null;
          if (data.anchor) {
            const id = data.anchor.id;
            const live = createSelectionAnchor(() => iframe.current, data.anchor.rect, active => send({ type: 'watchSelectionAnchor', id, active }));
            selectionAnchor.current = { id, update: live.update };
            selection.anchor = live.anchor;
          }
          context.onSelectionAction?.(selection);
        }
        else setError('无法确认这段选区的消息来源，请在单条回复中重新选择。');
        return;
      }
      if (data.type === 'selectionAnchor') {
        if (selectionAnchor.current?.id === data.id) selectionAnchor.current.update(data.rect);
        return;
      }
      if (data.type === 'copy') { void navigator.clipboard.writeText(data.text).catch(() => setError('浏览器未允许复制，请选择文字复制。')); return; }
      if (data.type === 'state' && data.stateKind === 'state') {
        const serialized = JSON.stringify(data.value);
        if (serialized.length > 256_000) return;
        if (!data.key || ['__proto__', 'constructor', 'prototype'].includes(data.key)) return;
        const state = { ...context.savedStates, ...states.current.get(currentScope.current) };
        if (Object.keys(state).length >= 256 && !Object.hasOwn(state, data.key)) return;
        state[data.key] = data.value;
        states.current.set(currentScope.current, state);
        context.onStateChange?.(state);
      }
    };
    window.addEventListener('message', listener); return () => {
      disposed = true; refreshVisualizationContext.current=()=>{}; latest.current.onVisualizationsChange?.([]); imageSequence.current++; visualizationArtifacts.clear(); visualizationFailures.clear(); visualizationAnchors.clear(); visualizationStateKeys.clear(); assetController.abort(); for(const controller of visualizationControllers)controller.abort(); selectionAnchor.current?.update(null); selectionAnchor.current = null;
      window.removeEventListener('message', listener); videoMetadata.dispose(); videoFrames.dispose(); frameRequests.clear(); media.dispose();
    };
  }, [scope, assetBaseUrl, inspectionId, inspectionMode]);

  useEffect(()=>{refreshVisualizationContext.current();},[visible,visualizationSource?.fileId,visualizationSource?.contentRevision,savedStates]);

  // Appearance is a separate command: changing it must not replace srcdoc or
  // publish the transcript again (which can restore saved DIL input state).
  useEffect(() => {
    if (!ready) return;
    iframe.current?.contentWindow?.postMessage({ channel: 'reados-renderer-v1', token: token.current,
      type: 'theme', scope, theme }, '*');
  }, [ready, scope, theme]);

  useEffect(() => {
    if (!ready || !assets) return;
    iframe.current?.contentWindow?.postMessage({ channel: 'reados-renderer-v1', token: token.current,
      type: 'render', scope, payload, resourceVersion: JSON.stringify(files.map(file => [file.id, file.sandboxPath, file.contentRevision])),
      dil: assets.dil, states: savedStates ?? states.current.get(scope) ?? {}, visible: latest.current.visible,
      asyncQuestionControls: { enabled: asyncQuestionEnabled, disabled: asyncQuestionDisabled },
      forkControls: { enabled: forkEnabled, disabled: forkDisabled,
        messageID: forking?.scope === scope ? forking.messageID : null } }, '*');
  }, [ready, assets, payload, scope, files, savedStates, forkEnabled, forkDisabled, forking, asyncQuestionEnabled, asyncQuestionDisabled]);

  useEffect(() => {
    if (!ready || !assets) return;
    iframe.current?.contentWindow?.postMessage({ channel: 'reados-renderer-v1', token: token.current,
      type: 'visibility', scope, visible }, '*');
    if (!visible) { imageSequence.current++; setImageSession(null); }
  }, [ready, assets, scope, visible]);


  useEffect(() => {
    if (!ready || !assets) return;
    let frame = 0, pointer: { x: number; y: number } | null = null;
    const checkPointer = () => {
      frame = 0;
      const element = iframe.current;
      if (!element?.isConnected || !pointer || !element.getClientRects().length) return;
      const rect = element.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0 || rect.right <= 0 || rect.bottom <= 0
        || rect.left >= window.innerWidth || rect.top >= window.innerHeight) return;
      if (pointer.x >= rect.left && pointer.x <= rect.right && pointer.y >= rect.top && pointer.y <= rect.bottom) return;
      // Some browsers omit pointerleave inside an opaque iframe. The parent
      // still receives movement outside its bounds; report only that boundary.
      element.contentWindow?.postMessage({ channel: 'reados-renderer-v1', token: token.current,
        type: 'turnNavigationPointerLeave', scope }, '*');
    };
    const onPointerMove = (event: PointerEvent) => {
      pointer = { x: event.clientX, y: event.clientY };
      if (!frame) frame = requestAnimationFrame(checkPointer);
    };
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    return () => { window.removeEventListener('pointermove', onPointerMove); if (frame) cancelAnimationFrame(frame); };
  }, [ready, assets, scope]);

  useEffect(() => {
    const previous = lastScrollRequest.current;
    lastScrollRequest.current = { scope, value: scrollToBottomRequest };
    if (!ready || !assets || previous.scope !== scope || scrollToBottomRequest === previous.value
      || !Number.isSafeInteger(scrollToBottomRequest) || (scrollToBottomRequest ?? 0) < 0) return;
    iframe.current?.contentWindow?.postMessage({ channel: 'reados-renderer-v1', token: token.current,
      type: 'scrollToBottom', scope, request: scrollToBottomRequest }, '*');
  }, [scrollToBottomRequest, ready, assets, scope]);

  return <div className="reados-transcript" data-renderer="reados-webbundle" data-presentation={presentation} data-theme={theme}>
    {error ? <div className="reados-transcript-error" role="alert">{error}</div>
      : emptyState ?? ((!ready || !assets) && <div className="reados-transcript-status" role="status">{presentation === 'document' ? '正在准备文档视图…' : '正在准备聊天视图…'}</div>)}
    {frameHTML && <iframe key={`${scope}:${inspectionId??''}:${inspectionMode}`} ref={iframe} className="reados-transcript-frame" title={title || 'ReadOS 聊天记录'} srcDoc={frameHTML}
      onPointerLeave={() => iframe.current?.contentWindow?.postMessage({ channel: 'reados-renderer-v1', token: token.current,
        type: 'turnNavigationPointerLeave', scope }, '*')}
      sandbox="allow-scripts" allow="fullscreen" referrerPolicy="no-referrer" />}
    {imageSession && imageSession.scope === scope && <ImageLightbox key={imageSession.sequence} images={imageSession.images}
      initialIndex={imageSession.index} onImageError={image => refreshImage.current(image)} onClose={() => {
        const closed = imageSession;
        const sequence = ++imageSequence.current;
        setImageSession(null);
        requestAnimationFrame(() => {
          if (currentScope.current !== closed.scope || imageSequence.current !== sequence) return;
          iframe.current?.contentWindow?.postMessage({ channel: 'reados-renderer-v1', token: token.current,
            type: 'imageViewerClosed', requestId: closed.requestId }, '*');
        });
      }} />}
  </div>;
}
function openExternal(raw: string) {
  try { const url = new URL(raw); if (/^https?:$/.test(url.protocol) && !url.username && !url.password && url.origin !== location.origin) window.open(url.href, '_blank', 'noopener,noreferrer'); } catch { /* Unsupported protocols never reach the browser. */ }
}
